import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { api } from '@/lib/axios';
import { Customer, Order } from '@/types';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Search,
  RefreshCw,
  Edit2,
  Trash2,
  MessageCircle,
  Instagram,
  ShoppingBag,
  DollarSign,
  MapPin,
  Sparkles,
} from 'lucide-react';

interface CustomerFormData {
  fullName: string;
  phoneNumber: string;
  email: string;
  instagram: string;
  address: string;
  notes: string;
  isWholesale: boolean;
}

const emptyForm: CustomerFormData = {
  fullName: '',
  phoneNumber: '',
  email: '',
  instagram: '',
  address: '',
  notes: '',
  isWholesale: false,
};

export default function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'wholesale' | 'repeat'>('all');
  const [isLoading, setIsLoading] = useState(true);

  // Estadísticas
  const [stats, setStats] = useState({
    totalCustomers: 0,
    wholesaleCount: 0,
    repeatCustomers: 0,
    totalRevenue: 0,
  });

  // Modal Alta / Modificación
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  // Modal Baja (Eliminar)
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal Historial de Pedidos
  const [selectedCustomerForOrders, setSelectedCustomerForOrders] = useState<Customer | null>(null);
  const [customerOrders, setCustomerOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  useEffect(() => {
    loadCustomers();
    loadStats();
  }, []);

  const loadCustomers = async (searchTerm = search) => {
    try {
      setIsLoading(true);
      const res = await api.get<Customer[]>('/customers', {
        params: searchTerm ? { search: searchTerm } : {},
      });
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('No se pudo cargar la lista de clientes desde la base de datos');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/customers/stats');
      if (res.data) {
        setStats(res.data);
      }
    } catch {
      // Si falla stats, calcular desde el listado local
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadCustomers(search);
  };

  // Abrir modal de Creación
  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setIsFormOpen(true);
  };

  // Abrir modal de Edición
  const handleOpenEdit = (customer: Customer) => {
    setEditingId(customer.id);
    setFormData({
      fullName: customer.fullName || customer.name || '',
      phoneNumber: customer.phoneNumber || customer.phone || '',
      email: customer.email || '',
      instagram: customer.instagram || '',
      address: customer.address || '',
      notes: customer.notes || '',
      isWholesale: !!customer.isWholesale,
    });
    setIsFormOpen(true);
  };

  // Guardar (Alta o Modificación)
  const handleSaveCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim()) {
      toast.error('El nombre del cliente es obligatorio');
      return;
    }

    try {
      setIsSaving(true);
      if (editingId) {
        // Modificación (PUT)
        await api.put(`/customers/${editingId}`, {
          id: editingId,
          fullName: formData.fullName.trim(),
          phoneNumber: formData.phoneNumber.trim() || null,
          email: formData.email.trim() || null,
          instagram: formData.instagram.trim() || null,
          address: formData.address.trim() || null,
          notes: formData.notes.trim() || null,
          isWholesale: formData.isWholesale,
        });
        toast.success('Cliente actualizado con éxito en la base de datos');
      } else {
        // Alta (POST)
        await api.post('/customers', {
          fullName: formData.fullName.trim(),
          phoneNumber: formData.phoneNumber.trim() || null,
          email: formData.email.trim() || null,
          instagram: formData.instagram.trim() || null,
          address: formData.address.trim() || null,
          notes: formData.notes.trim() || null,
          isWholesale: formData.isWholesale,
        });
        toast.success('Nuevo cliente registrado con éxito en la base de datos');
      }

      setIsFormOpen(false);
      loadCustomers();
      loadStats();
    } catch {
      toast.error('Ocurrió un error al guardar los datos del cliente');
    } finally {
      setIsSaving(false);
    }
  };

  // Baja (DELETE)
  const handleDeleteCustomer = async () => {
    if (!deletingCustomer) return;
    try {
      setIsDeleting(true);
      await api.delete(`/customers/${deletingCustomer.id}`);
      toast.success(`Cliente "${deletingCustomer.fullName || deletingCustomer.name}" eliminado`);
      setDeletingCustomer(null);
      loadCustomers();
      loadStats();
    } catch {
      toast.error('No se pudo eliminar el cliente');
    } finally {
      setIsDeleting(false);
    }
  };

  // Ver Pedidos del Cliente
  const handleViewOrders = async (customer: Customer) => {
    setSelectedCustomerForOrders(customer);
    try {
      setIsLoadingOrders(true);
      const res = await api.get<Order[]>(`/customers/${customer.id}/orders`);
      setCustomerOrders(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('No se pudieron obtener los pedidos del cliente');
      setCustomerOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  // Filtrado en memoria
  const filteredCustomers = customers.filter((c) => {
    if (filterType === 'wholesale') return !!c.isWholesale;
    if (filterType === 'repeat') return (c.orderCount ?? c.order_count ?? 0) > 1;
    return true;
  });

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      {/* Encabezado Principal */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-3">
            <Users className="h-8 w-8 text-primary" /> Clientes y CRM
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Base de datos de compradores de la tienda y pedidos personalizados del taller.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadCustomers();
              loadStats();
            }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" /> Actualizar
          </Button>
          <Button
            onClick={handleOpenCreate}
            size="sm"
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-xs"
          >
            <UserPlus className="h-4 w-4" /> Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Tarjetas de Métricas Rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card shadow-2xs border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground flex items-center justify-between">
              Total Clientes
              <Users className="h-4 w-4 text-primary" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats.totalCustomers || customers.length}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Registrados en BD</p>
          </CardContent>
        </Card>

        <Card className="bg-card shadow-2xs border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground flex items-center justify-between">
              Facturación Acumulada
              <DollarSign className="h-4 w-4 text-emerald-500" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              ${(stats.totalRevenue || customers.reduce((acc, c) => acc + (c.totalSpent || c.total_spent || 0), 0)).toLocaleString('es-AR')}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Ventas directas y web</p>
          </CardContent>
        </Card>

        <Card className="bg-card shadow-2xs border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground flex items-center justify-between">
              Clientes Recurrentes
              <ShoppingBag className="h-4 w-4 text-blue-500" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats.repeatCustomers || customers.filter(c => (c.orderCount ?? c.order_count ?? 0) > 1).length}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">+ de 1 pedido realizado</p>
          </CardContent>
        </Card>

        <Card className="bg-card shadow-2xs border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground flex items-center justify-between">
              Mayoristas / Distribuidores
              <Sparkles className="h-4 w-4 text-amber-500" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats.wholesaleCount || customers.filter(c => c.isWholesale).length}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Tarifa diferenciada</p>
          </CardContent>
        </Card>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-card p-4 rounded-xl border border-border/70 shadow-2xs">
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, WhatsApp, email o Instagram..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 text-sm"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm" className="h-10 px-3">
            Buscar
          </Button>
        </form>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <Button
            variant={filterType === 'all' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilterType('all')}
            className="text-xs rounded-lg"
          >
            Todos ({customers.length})
          </Button>
          <Button
            variant={filterType === 'wholesale' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilterType('wholesale')}
            className="text-xs rounded-lg"
          >
            Mayoristas ({customers.filter(c => c.isWholesale).length})
          </Button>
          <Button
            variant={filterType === 'repeat' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilterType('repeat')}
            className="text-xs rounded-lg"
          >
            Recurrentes ({customers.filter(c => (c.orderCount ?? c.order_count ?? 0) > 1).length})
          </Button>
        </div>
      </div>

      {/* Listado de Clientes */}
      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground space-y-2">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-primary" />
          <p className="text-sm">Cargando base de datos de clientes...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card className="p-12 text-center space-y-4 border-dashed">
          <Users className="h-12 w-12 text-muted-foreground/40 mx-auto" />
          <div>
            <h3 className="text-lg font-bold">No se encontraron clientes</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {search
                ? `No hay clientes que coincidan con "${search}".`
                : 'Todavía no hay clientes registrados en la base de datos.'}
            </p>
          </div>
          <Button onClick={handleOpenCreate} className="gap-2">
            <UserPlus className="h-4 w-4" /> Agregar Primer Cliente
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredCustomers.map((c) => {
            const displayName = c.fullName || c.name || 'Sin Nombre';
            const displayPhone = c.phoneNumber || c.phone;
            const spent = (c.totalSpent ?? c.total_spent ?? 0);
            const orders = (c.orderCount ?? c.order_count ?? 0);
            const initials = displayName
              .split(' ')
              .map((n) => n[0])
              .slice(0, 2)
              .join('')
              .toUpperCase();

            return (
              <Card
                key={c.id}
                className="bg-card shadow-2xs border-border/70 hover:border-primary/40 transition-all flex flex-col justify-between overflow-hidden"
              >
                <CardContent className="p-5 space-y-4">
                  {/* Cabecera del Cliente */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="h-11 w-11 border border-border/80 shrink-0 bg-primary/10 text-primary font-bold">
                        <AvatarFallback>{initials || 'C'}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h3 className="font-bold text-base text-foreground truncate" title={displayName}>
                          {displayName}
                        </h3>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {c.isWholesale && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold border-amber-500/20">
                              Mayorista
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">ID #{c.id}</span>
                        </div>
                      </div>
                    </div>

                    {/* Acciones de Edición y Baja */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => handleOpenEdit(c)}
                        title="Editar datos del cliente"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setDeletingCustomer(c)}
                        title="Eliminar cliente"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Datos de Contacto y Links Rápidos */}
                  <div className="space-y-1.5 text-xs text-muted-foreground pt-1 border-t border-border/50">
                    {displayPhone ? (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 truncate">
                          <MessageCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                          {displayPhone}
                        </span>
                        <a
                          href={`https://wa.me/${displayPhone.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 dark:text-emerald-400 font-semibold hover:underline shrink-0"
                        >
                          WhatsApp
                        </a>
                      </div>
                    ) : (
                      <div className="text-muted-foreground/60 italic">Sin teléfono registrado</div>
                    )}

                    {c.email && (
                      <div className="truncate text-muted-foreground" title={c.email}>
                        ✉️ {c.email}
                      </div>
                    )}

                    {c.instagram && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-muted-foreground truncate">
                          <Instagram className="h-3.5 w-3.5 text-pink-500 shrink-0" />
                          @{c.instagram.replace('@', '')}
                        </span>
                        <a
                          href={`https://instagram.com/${c.instagram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-600 dark:text-pink-400 font-semibold hover:underline shrink-0"
                        >
                          Ver Perfil
                        </a>
                      </div>
                    )}

                    {c.address && (
                      <div className="flex items-start gap-1.5 text-muted-foreground pt-0.5 truncate" title={c.address}>
                        <MapPin className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                        <span className="truncate">{c.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Métricas del Cliente y Botón de Historial */}
                  <div className="pt-3 border-t border-border/60 flex items-center justify-between text-xs">
                    <div>
                      <p className="text-muted-foreground">Total Gastado</p>
                      <p className="font-bold text-sm text-foreground">${spent.toLocaleString('es-AR')}</p>
                    </div>

                    <div className="text-right">
                      <p className="text-muted-foreground">Pedidos</p>
                      <button
                        onClick={() => handleViewOrders(c)}
                        className="font-bold text-sm text-primary hover:underline flex items-center gap-1 justify-end ml-auto"
                        title="Ver historial de pedidos"
                      >
                        {orders} {orders === 1 ? 'pedido' : 'pedidos'} →
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Modal de Alta / Modificación de Cliente ── */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Completá los datos del cliente para registrarlo en la base de datos de MilaOS.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCustomer} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="form-fullName" className="text-xs font-semibold">
                Nombre y Apellido *
              </Label>
              <Input
                id="form-fullName"
                required
                placeholder="Ej. Paula Gómez"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="form-phone" className="text-xs font-semibold">
                  WhatsApp / Teléfono
                </Label>
                <Input
                  id="form-phone"
                  placeholder="Ej. 11 5555-4444"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="form-instagram" className="text-xs font-semibold">
                  Instagram
                </Label>
                <Input
                  id="form-instagram"
                  placeholder="Ej. miladesign"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="form-email" className="text-xs font-semibold">
                Correo Electrónico
              </Label>
              <Input
                id="form-email"
                type="email"
                placeholder="cliente@ejemplo.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="form-address" className="text-xs font-semibold">
                Dirección / Localidad
              </Label>
              <Input
                id="form-address"
                placeholder="Ej. Av. Santa Fe 1234, CABA"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="form-notes" className="text-xs font-semibold">
                Notas internas
              </Label>
              <Input
                id="form-notes"
                placeholder="Ej. Prefiere piezas en acabado blanco mate..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>

            <div className="pt-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                <input
                  type="checkbox"
                  checked={formData.isWholesale}
                  onChange={(e) => setFormData({ ...formData, isWholesale: e.target.checked })}
                  className="rounded border-border text-primary focus:ring-primary w-4 h-4"
                />
                <span>Es Cliente Mayorista / Revendedor</span>
              </label>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsFormOpen(false)}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving} className="gap-2">
                {isSaving ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Cliente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Modal de Confirmación de Baja (Eliminar) ── */}
      <Dialog open={!!deletingCustomer} onOpenChange={(open) => !open && setDeletingCustomer(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Dar de baja cliente
            </DialogTitle>
            <DialogDescription className="text-xs">
              ¿Estás seguro de que deseás eliminar a{' '}
              <strong className="text-foreground">
                {deletingCustomer?.fullName || deletingCustomer?.name}
              </strong>{' '}
              de la base de datos? Sus pedidos históricos se conservarán para las estadísticas del taller.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-3">
            <Button
              variant="outline"
              onClick={() => setDeletingCustomer(null)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCustomer}
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar Cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal de Historial de Pedidos del Cliente ── */}
      <Dialog open={!!selectedCustomerForOrders} onOpenChange={(open) => !open && setSelectedCustomerForOrders(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <ShoppingBag className="h-5 w-5 text-primary" />
              Historial de Pedidos de {selectedCustomerForOrders?.fullName || selectedCustomerForOrders?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Listado de compras registradas en el taller para este cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-3">
            {isLoadingOrders ? (
              <div className="text-center py-8 text-muted-foreground space-y-2">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto text-primary" />
                <p className="text-xs">Consultando pedidos...</p>
              </div>
            ) : customerOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Este cliente todavía no tiene pedidos registrados en el sistema.
              </div>
            ) : (
              <div className="space-y-3">
                {customerOrders.map((ord: any) => (
                  <Card key={ord.id} className="p-4 border-border/70 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">Pedido #{ord.id}</span>
                        <Badge variant="outline" className="text-[11px] capitalize">
                          {ord.status === 'pending'
                            ? 'Pendiente'
                            : ord.status === 'in_production'
                            ? 'En Producción'
                            : ord.status === 'ready'
                            ? 'Listo'
                            : ord.status === 'delivered'
                            ? 'Entregado'
                            : ord.status}
                        </Badge>
                      </div>
                      <span className="font-bold text-sm text-primary">
                        ${(ord.total || 0).toLocaleString('es-AR')}
                      </span>
                    </div>

                    <div className="text-xs text-muted-foreground flex items-center justify-between">
                      <span>{new Date(ord.createdAt).toLocaleDateString('es-AR')}</span>
                      <span>Método: {ord.deliveryMethod || 'Taller'}</span>
                    </div>

                    {ord.items && ord.items.length > 0 && (
                      <div className="bg-muted/40 p-2.5 rounded-lg space-y-1.5 text-xs border border-border/50">
                        <p className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
                          Items del pedido:
                        </p>
                        {ord.items.map((it: any, iIdx: number) => (
                          <div key={iIdx} className="flex justify-between items-center">
                            <span>
                              • {it.productName} x{it.quantity}
                              {it.filamentColor && (
                                <span className="text-muted-foreground ml-1.5">
                                  ({it.filamentColor})
                                </span>
                              )}
                              {it.variantNotes && (
                                <span className="text-amber-700 dark:text-amber-400 ml-1.5 font-medium">
                                  [{it.variantNotes}]
                                </span>
                              )}
                            </span>
                            <span className="font-medium">
                              ${((it.unitPrice || 0) * it.quantity).toLocaleString('es-AR')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setSelectedCustomerForOrders(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
